import { ProgressService, WindowService } from '@ups/xplat/core';
import { FileDto, IFileData } from '@ups/xplat/api/dto';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AttachmentVpService } from '@ups/xplat/api/services';
import { SpartaFileService } from '@ups/files';
import { guid } from '@ups/xplat/utils';
import { IDynamicModel, IDynamicResponseBody } from '../models';

/** What the file and signature helpers read from DynamicRenderService; the service passes itself. */
export interface IDynamicRenderFilesHost {
  win: WindowService;
  spartaFileService: SpartaFileService;
  attachmentService: AttachmentVpService;
  isSignatureEmpty: () => boolean;
  signatureUploadFile: FileDto | null;
  signatureBase64: () => Promise<string>;
  addAttachmentForField(name: string, attachment: FileDto): void;
  createAndUploadFileForConfig(
    filename: string,
    content: string,
    contentType: string,
    config: IDynamicModel
  ): Promise<FileDto | null>;
}

/**
 * Calls `add` for each file a saved response's file fields hold (WI 31925: split out of
 * DynamicRenderService.checkResponseForAttachments, unchanged).
 */
export function forEachDynamicResponseAttachment(
  response: IDynamicResponseBody,
  add: (controlName: string, attachment: FileDto) => void
) {
  (response.dynamicControlResponses || []).forEach((res) => {
    if (res.responseContentJson) {
      const controlName = res.dynamicControlName;
      const fileAttachments = JSON.parse(res.responseContentJson);
      if (fileAttachments && controlName) {
        if (Array.isArray(fileAttachments)) {
          for (const fileAttachment of fileAttachments) {
            add(controlName, fileAttachment);
          }
        } else {
          add(controlName, fileAttachments);
        }
      }
    }
  });
}

/** Uploads a file for a file field to Sparta and records it as that field's attachment. */
export function createAndUploadDynamicFile(
  host: IDynamicRenderFilesHost,
  filename: string,
  content: string,
  contentType: string,
  config: IDynamicModel
): Promise<FileDto | null> {
  return new Promise((resolve) => {
    const fileDto: FileDto = new FileDto();
    fileDto.name = filename;
    if (typeof content === 'string') {
      fileDto.contentBase64 = content;
    } else {
      fileDto.content = content;
    }
    fileDto.contentType = contentType;
    fileDto.directory =
      config.options && config.options.directory
        ? config.options.directory
        : 'editModal';
    fileDto.bActive = true;

    host.spartaFileService
      .uploadFile(fileDto)
      .then((f) => {
        if (config.formControlName) {
          host.addAttachmentForField(config.formControlName, f);
        }
        resolve(f);
      })
      .catch(() => {
        host.win.alert('An error has occurred.');
        resolve(null);
      });
  });
}

/** A file by attachment id, from Sparta or Viewpoint (DynamicRenderService.getFile, unchanged). */
export function getDynamicFile(
  host: IDynamicRenderFilesHost,
  attachmentID: string,
  useSparta?: boolean,
  spartaDownload?: boolean
): Promise<string | IFileData | null> {
  return new Promise((resolve) => {
    if (attachmentID) {
      if (useSparta) {
        host.spartaFileService.getFileWithContent(`${attachmentID}`).then(
          (data) => {
            resolve(<IFileData>data);
          },
          () => {
            resolve(null);
          }
        );
      }
      if (spartaDownload) {
        host.spartaFileService.downloadFileFromSparta(`${attachmentID}`).then(
          (data) => {
            resolve(data.Data || null);
          },
          () => {
            resolve(null);
          }
        );
      } else {
        host.attachmentService
          .getFileByAttachmentId(`${attachmentID}`)
          .subscribe((data: IFileData) => resolve(data));
      }
    }
  });
}

/**
 * Uploads the signature before a response is submitted (DynamicRenderService.prepareToSubmitResponse, unchanged).
 * Never settles when the pad is empty or the upload fails: the submit stops there, as before.
 */
export function prepareDynamicSignatureForSubmit(
  host: IDynamicRenderFilesHost,
  progress: ProgressService,
  controls: Array<IDynamicModel>
): Promise<void> {
  return new Promise((resolve) => {
    const signatureControl = controls.find((c) => c.type === 'signature');
    if (signatureControl) {
      // check if pad is signed
      if (host.isSignatureEmpty()) {
        progress.toggleSpinner(false);
        host.win.alert({
          title: 'Signature Required!',
          message: `Your signature is required to submit this form.`,
          okButtonText: 'Ok',
        });
        return;
      } else if (!host.signatureUploadFile) {
        progress.toggleSpinner(true);
        // get base64 signature image
        host.signatureBase64().then((signatureBase64) => {
          // create filedto of signature image
          host
            .createAndUploadFileForConfig(
              `Signature-${guid()}.png`,
              signatureBase64,
              'image/png',
              signatureControl
            )
            .then((fileDto) => {
              if (fileDto) {
                host.signatureUploadFile = fileDto;
                resolve();
              } else {
                progress.toggleSpinner(false);
              }
            });
        });
      } else {
        resolve();
      }
    } else {
      resolve();
    }
  });
}
